import styled, { css } from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";

export type V3DrawerSide = "left" | "right";

const BOTTOM_SHEET_BREAKPOINT = 640;

export const DrawerBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  background: ${({ theme }) => theme.colors.ui.overlay.medium};
  opacity: ${({ $open, theme }) => ($open ? theme.opacity.default : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: opacity ${({ theme }) => theme.motion.duration.standard}ms
      ${({ theme }) => theme.motion.easing.standard},
    visibility 0ms linear
      ${({ $open, theme }) => ($open ? 0 : theme.motion.duration.standard)}ms;
`;

interface DrawerPanelProps {
  $open: boolean;
  $width: number;
  $side: V3DrawerSide;
}

const panelEdge = css<DrawerPanelProps>`
  ${({ $side, theme }) =>
    $side === "right"
      ? css`
          right: 0;
          border-left: ${theme.stroke.md}px solid
            ${theme.colors.ui.border.subtle};
        `
      : css`
          left: 0;
          border-right: ${theme.stroke.md}px solid
            ${theme.colors.ui.border.subtle};
        `}
`;

export const DrawerPanel = styled.aside<DrawerPanelProps>`
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.popover};
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width}px;
  max-width: calc(100vw - ${({ theme }) => theme.space["4xl"]}px);
  background: ${({ theme }) => theme.colors.ui.background.raised};
  box-shadow: ${({ theme }) => theme.elevation.high};
  ${panelEdge}
  transform: translateX(
    ${({ $open, $side }) => {
    if ($open) return "0";
    return $side === "right" ? "100%" : "-100%";
  }}
  );
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: transform ${({ theme }) => theme.motion.duration.moderate}ms
      ${({ theme }) => theme.motion.easing.decelerate},
    visibility 0ms linear
      ${({ $open, theme }) => ($open ? 0 : theme.motion.duration.moderate)}ms;

  @media (max-width: ${BOTTOM_SHEET_BREAKPOINT}px) {
    top: auto;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    max-height: 85vh;
    border: none;
    border-top: ${({ theme }) => theme.stroke.md}px solid
      ${({ theme }) => theme.colors.ui.border.subtle};
    border-top-left-radius: ${({ theme }) => theme.radius.lg}px;
    border-top-right-radius: ${({ theme }) => theme.radius.lg}px;
    transform: translateY(${({ $open }) => ($open ? "0" : "100%")});
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.lg}px;
  padding: ${({ theme }) => theme.space.card.inset}px
    ${({ theme }) => theme.space["2xl"]}px;
  border-bottom: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => theme.colors.ui.border.subtle};
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  ${typographyCSS("heading.sm")}
  line-height: 26px;
  color: ${({ theme }) => theme.colors.ui.text.primary};
`;

export const DrawerCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ theme }) => theme.size.icon.lg}px;
  height: ${({ theme }) => theme.size.icon.lg}px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.ui.icon.secondary};
  border-radius: ${({ theme }) => theme.radius.sm}px;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.duration.instant}ms
      ${({ theme }) => theme.motion.easing.standard},
    color ${({ theme }) => theme.motion.duration.instant}ms
      ${({ theme }) => theme.motion.easing.standard};

  &:hover {
    background: ${({ theme }) => theme.colors.ui.background.inset};
    color: ${({ theme }) => theme.colors.ui.icon.primary};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.stroke.focus}px solid
      ${({ theme }) => theme.colors.ui.border.focus};
    outline-offset: ${({ theme }) => theme.space["3xs"]}px;
  }
`;

export const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg}px;
  padding: ${({ theme }) => theme.space["2xl"]}px;
  color: ${({ theme }) => theme.colors.ui.text.secondary};
`;
